import React, { useState, useEffect } from "react";
import { Certif } from "../../CertificatesPage";
import { formatDate } from '../../utils/dateUtils';
import './Certificates.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import ColumnFilter from '../Filters/ColumnFilter';
import { useResizableColumns } from '../../hooks/useResizableColumns';

const exportToExcel = (data: Certif[], visibleColumns: string[], hideApplicantFields: boolean = false) => {
    const columnMapping: { [key: string]: string } = {
        'cert_reg_data': 'Дата регистрации сертификата',
        'cert_finish_data': 'Дата окончания сертификата',
        'cert_status': 'Статус сертификата',
        'cert_schema': 'Схема сертификации',
        'comment_apts': 'Комментарий АПТС',
        'full_comment_apts': 'Полный комментарий АПТС',
        'cert_reg_number': 'Номер сертификата',
        ...(hideApplicantFields ? {} : {
            'applicant_shortname': 'Заявитель',
            'applicant_inn': 'ИНН заявителя',
            'applicant_region': 'Регион заявителя'
        }),
        'manufacture_fullname': 'Изготовитель',
        'manufacture_inn': 'ИНН изготовителя',
        'region_manufacturer': 'Регион изготовителя',
        'product_fullname': 'Продукция',
        'technical_docs': 'Изготовлено в соответствии',
        'regulatory_documentation': 'Проверена на соответствие',
        'os_name': 'Орган по сертификации',
        'os_number': 'Номер органа по сертификации',
        'lan_name_1': 'Названия лабораторий',
        'lab_number_1': 'Номера лабораторий'
    };

    // Функция для объединения лабораторий через точку с запятой
    const combineLabs = (item: Certif, field: string) => {
        const labs = [];
        for (let i = 1; i <= 3; i++) {
            const value = (item as any)[`${field}_${i}`];
            if (value && value !== 'Отсутствует') {
                labs.push(value);
            }
        }
        return labs.join('; ');
    };

    const formattedData = data.map(item => {
        const formattedItem: { [key: string]: any } = {};
        
        visibleColumns.forEach(columnKey => {
            const label = columnMapping[columnKey];
            if (label) {
                if (columnKey === 'cert_reg_data' || columnKey === 'cert_finish_data') {
                    formattedItem[label] = formatDate(item[columnKey as keyof Certif] || '');
                } else if (columnKey.startsWith('lan_name_') || columnKey.startsWith('lab_number_')) {
                    if (columnKey === 'lan_name_1') {
                        formattedItem['Названия лабораторий'] = combineLabs(item, 'lan_name');
                    } else if (columnKey === 'lab_number_1') {
                        formattedItem['Номера лабораторий'] = combineLabs(item, 'lab_number');
                    }
                } else {
                    formattedItem[label] = item[columnKey as keyof Certif] || '';
                }
            }
        });
        
        return formattedItem;
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Сертификаты");

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(dataBlob, 'Сертификаты.xlsx');
};



interface CertificatesProps {
    filteredLabList: (Certif & { _cert_type?: string })[];
    onSort: (field: keyof Certif) => void;
    getSortIcon: (field: keyof Certif) => string;
    showTypeColumn?: boolean;
    onFilterToggle?: () => void;
    isFilterOpen?: boolean;
    onDateFilterToggle?: () => void;
    hideApplicantFields?: boolean;
    onRowClick?: (cert: Certif & { _cert_type?: string }) => void;
}

// Перетаскивание столбцов
interface ColumnConfig {
    key: keyof Certif | '_cert_type';
    label: string;
    render?: (value: any, item: Certif & { _cert_type?: string }) => React.ReactNode;
}

function Certificates({ filteredLabList, onSort, getSortIcon, showTypeColumn, onFilterToggle, isFilterOpen, onDateFilterToggle, hideApplicantFields = false, onRowClick }: CertificatesProps) {
    const baseDefaultColumnOrder = [
        'cert_reg_data',
        'cert_finish_data',
        'cert_status',
        'cert_schema',
        'comment_apts', 
        'full_comment_apts',
        'cert_reg_number',
        ...(hideApplicantFields ? [] : ['applicant_shortname', 'applicant_inn']),
        'manufacture_fullname',
        'manufacture_inn',
        'region_manufacturer',
        'product_fullname',
        'technical_docs',
        'regulatory_documentation',
        'os_name',
        'os_number',
        'lan_name_1',
        'lab_number_1',
        ...(hideApplicantFields ? [] : ['applicant_region'])
    ];

    const defaultColumnOrder = (showTypeColumn ? ['_cert_type', ...baseDefaultColumnOrder] : baseDefaultColumnOrder) as string[];

    const [columnOrder, setColumnOrder] = useState<string[]>(() => {
        const saved = localStorage.getItem('certificates-column-order');
        if (saved) {
            return JSON.parse(saved);
        } else {
            localStorage.setItem('certificates-column-order', JSON.stringify(defaultColumnOrder));
            return defaultColumnOrder;
        }
    });

    const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
        const saved = localStorage.getItem('certificates-visible-columns');
        if (saved) {
            return JSON.parse(saved);
        } else {
            localStorage.setItem('certificates-visible-columns', JSON.stringify(defaultColumnOrder));
            return defaultColumnOrder;
        }
    });

    const [draggedColumn, setDraggedColumn] = useState<string>('');

    const typeColumn: ColumnConfig = {
        key: '_cert_type',
        label: 'Тип',
        render: (value, item) => {
            const text = item._cert_type || '';
            const color = text === 'Добровольная' ? '#6f42c1' : '#0d6efd';
            return (
                <span style={{
                    color: 'white',
                    backgroundColor: color,
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap'
                }}>
                    {text}
                </span>
            );
        }
    };

    const applicantColumns: ColumnConfig[] = hideApplicantFields ? [] : [
        { key: 'applicant_shortname', label: 'Заявитель' },
        { key: 'applicant_inn', label: 'ИНН заявителя' }
    ];

    const applicantRegionColumn: ColumnConfig[] = hideApplicantFields ? [] : [
        { key: 'applicant_region', label: 'Регион заявителя' }
    ];

    const columns: ColumnConfig[] = [
        ...(showTypeColumn ? [typeColumn] : []),
        { key: 'cert_reg_data', label: 'Дата регистрации сертификата' },
        { key: 'cert_finish_data', label: 'Дата окончания сертификата' },
        { 
            key: 'cert_status', 
            label: 'Статус сертификата',
            render: (value) => {
                const statusColors: { [key: string]: string } = {
                    'Действует': '#28a745',
                    'Приостановлен': '#ffc107',
                    'Отозван': '#dc3545',
                    'Истек': '#6c757d'
                };
                const color = statusColors[value] || '#6c757d';
                return (
                    <span style={{ 
                        color: 'white', 
                        backgroundColor: color, 
                        padding: '2px 6px', 
                        borderRadius: '3px', 
                        fontSize: '11px',
                        fontWeight: '500'
                    }}>
                        {value}
                    </span>
                );
            }
        },
        { 
            key: 'cert_schema', 
            label: 'Схема сертификации',
            render: (value) => (
                <span style={{ 
                    backgroundColor: '#e9ecef', 
                    padding: '2px 6px', 
                    borderRadius: '3px', 
                    fontSize: '11px',
                    fontWeight: '500'
                }}>
                    {value}
                </span>
            )
        },
        { key: 'comment_apts', label: 'Комментарий АПТС' },
        { key: 'full_comment_apts', label: 'Полный комментарий АПТС' },
        { 
            key: 'cert_reg_number', 
            label: 'Номер сертификата',
            render: (value, item) => (
                item.cert_link ? (
                    <a 
                        href={item.cert_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                        {value}
                    </a>
                ) : (
                    value
                )
            )
        },
        ...applicantColumns,
        { key: 'manufacture_fullname', label: 'Изготовитель' },
        { key: 'manufacture_inn', label: 'ИНН изготовителя' },
        { key: 'region_manufacturer', label: 'Регион изготовителя' },
        { key: 'product_fullname', label: 'Продукция' },
        { key: 'technical_docs', label: 'Изготовлено в соответствии' },
        { key: 'regulatory_documentation', label: 'Проверена на соответствие' },
        { key: 'os_name', label: 'Орган по сертификации' },
        { key: 'os_number', label: 'Номер органа по сертификации' },
        { 
            key: 'lan_name_1', 
            label: 'Названия лабораторий',
            render: (value, item) => {
                const labs = [];
                for (let i = 1; i <= 3; i++) {
                    const name = (item as any)[`lan_name_${i}`];
                    if (name && name !== 'Отсутствует') {
                        labs.push(name);
                    }
                }
                return labs.join('; ');
            }
        },
        { 
            key: 'lab_number_1', 
            label: 'Номера лабораторий',
            render: (value, item) => {
                const labs = [];
                for (let i = 1; i <= 3; i++) {
                    const number = (item as any)[`lab_number_${i}`];
                    if (number && number !== 'Отсутствует') {
                        labs.push(number);
                    }
                }
                return labs.join('; ');
            }
        },
        ...applicantRegionColumn
    ];

    const handleDragStart = (e: React.DragEvent, columnKey: string) => {
        setDraggedColumn(columnKey);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e: React.DragEvent, targetColumnKey: string) => {
        e.preventDefault();
        
        if (!draggedColumn || draggedColumn === targetColumnKey) {
            return;
        }

        const newOrder = [...columnOrder];
        const draggedIndex = newOrder.indexOf(draggedColumn);
        const targetIndex = newOrder.indexOf(targetColumnKey);

        newOrder.splice(draggedIndex, 1);
        newOrder.splice(targetIndex, 0, draggedColumn);

        setColumnOrder(newOrder);
        localStorage.setItem('certificates-column-order', JSON.stringify(newOrder));
        setDraggedColumn('');
    };

    const handleDragEnd = () => {
        setDraggedColumn('');
    };

    const handleColumnVisibilityChange = (newVisibleColumns: string[]) => {
        setVisibleColumns(newVisibleColumns);
        localStorage.setItem('certificates-visible-columns', JSON.stringify(newVisibleColumns));
        
        let newColumnOrder: string[] = [];
        
        columnOrder.forEach(col => {
            if (newVisibleColumns.includes(col)) {
                newColumnOrder.push(col);
            }
        });
        
        newVisibleColumns.forEach(col => {
            if (!newColumnOrder.includes(col)) {
                const originalIndex = defaultColumnOrder.indexOf(col);
                if (originalIndex !== -1) {
                    let insertIndex = newColumnOrder.length; 
                    
                    for (let i = 0; i < newColumnOrder.length; i++) {
                        const currentOriginalIndex = defaultColumnOrder.indexOf(newColumnOrder[i]);
                        if (currentOriginalIndex > originalIndex) {
                            insertIndex = i;
                            break;
                        }
                    }
                    
                    newColumnOrder.splice(insertIndex, 0, col);
                } else {
                    newColumnOrder.push(col);
                }
            }
        });
        
        setColumnOrder(newColumnOrder);
        localStorage.setItem('certificates-column-order', JSON.stringify(newColumnOrder));
    };

    const resetColumnSettings = () => {
        setVisibleColumns(defaultColumnOrder);
        setColumnOrder(defaultColumnOrder);
        localStorage.setItem('certificates-visible-columns', JSON.stringify(defaultColumnOrder));
        localStorage.setItem('certificates-column-order', JSON.stringify(defaultColumnOrder));
    };

    const getColumnByKey = (key: string) => columns.find(col => col.key === (key as any));

    const filteredColumnOrder = columnOrder.filter(col => visibleColumns.includes(col));

    const { getStyleForColumn, handleMouseDown, resetColumnWidths } = useResizableColumns(filteredColumnOrder, {
        storageKey: 'certificates-column-widths',
        minWidthPx: 100,
        maxWidthPx: 800
    });

    return (
        <div className="table-container">
            <div className="table-controls">
                <button onClick={() => exportToExcel(filteredLabList, visibleColumns, hideApplicantFields)} className="export-button-certificates">
                    Экспортировать в Excel
                </button>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {onFilterToggle && (
                        <button 
                            onClick={onFilterToggle}
                            style={{
                                background: 'none',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                padding: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46 22,3"/>
                            </svg>
                            Фильтр
                        </button>
                    )}
                    {onDateFilterToggle && (
                        <button 
                            onClick={onDateFilterToggle}
                            style={{
                                background: 'none',
                                border: '1px solid #007bff',
                                borderRadius: '4px',
                                padding: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: '#007bff'
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                <line x1="16" y1="2" x2="16" y2="6"/>
                                <line x1="8" y1="2" x2="8" y2="6"/>
                                <line x1="3" y1="10" x2="21" y2="10"/>
                            </svg>
                            Фильтр по дате
                        </button>
                    )}
                    <ColumnFilter
                        columns={columns}
                        visibleColumns={visibleColumns}
                        onColumnVisibilityChange={handleColumnVisibilityChange}
                        defaultColumns={defaultColumnOrder}
                        onReset={resetColumnSettings}
                    />
                </div>
            </div>

            {filteredLabList.length === 0 ? (
                <div className="no-data-message">
                    <p>Нет данных для отображения</p>
                </div>
            ) : (
                <table className="lab-table" style={{ fontSize: '13px' }}>
                    <thead>        
                        <tr className='table-header_detail'>
                            {filteredColumnOrder.map((columnKey) => {
                                const column = getColumnByKey(columnKey);
                                if (!column) return null;
                                const isTypeCol = column.key === '_cert_type';

                                return (
                                    <th 
                                        key={columnKey}
                                        className={`table-header-item ${column.key === '_cert_type' ? 'sticky-left' : ''}`}
                                        onClick={() => { if (!isTypeCol) onSort(column.key as keyof Certif); }} 
                                        style={{ 
                                            padding: '4px',
                                            cursor: 'grab',
                                            backgroundColor: draggedColumn === columnKey ? '#005a94' : undefined,
                                            opacity: draggedColumn && draggedColumn !== columnKey ? 0.7 : 1,
                                            ...getStyleForColumn(columnKey)
                                        }}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, columnKey)}
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDrop(e, columnKey)}
                                        onDragEnd={handleDragEnd}
                                    >
                                        {column.label} {!isTypeCol ? getSortIcon(column.key as keyof Certif) : ''}
                                        <span
                                            className="col-resizer"
                                            onMouseDown={(e) => handleMouseDown(columnKey, e)}
                                        />
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredLabList.map((lab, index) => (
                            <tr
                                className={`table-body_detail${onRowClick ? ' table-row-clickable' : ''}`}
                                key={lab.cert_reg_number || index}
                                onClick={() => onRowClick?.(lab)}
                                onKeyDown={(event) => {
                                    if (!onRowClick) return;
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        onRowClick(lab);
                                    }
                                }}
                                tabIndex={onRowClick ? 0 : -1}
                                role={onRowClick ? 'button' : undefined}
                                aria-label={
                                    onRowClick
                                        ? `Подробнее о сертификате ${lab.cert_reg_number || ''}`
                                        : undefined
                                }
                            >
                                {filteredColumnOrder.map((columnKey) => {
                                    const column = getColumnByKey(columnKey);
                                    if (!column) return null;

                                    const value = (lab as any)[column.key];
                                    return (
                                        <td key={columnKey} className={`table-body-item ${column.key === '_cert_type' ? 'sticky-left' : ''}`} style={{ padding: '4px', ...getStyleForColumn(columnKey) }}>
                                            {column.render ? column.render(value, lab) : 
                                                (column.key === 'cert_reg_data' || column.key === 'cert_finish_data') ? formatDate(value || '') : value}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default Certificates;
